/**
 * Individual notification row — displays icon, title, body, and timestamp.
 * Unread items have a highlighted background with a dot indicator.
 */
import { useCallback } from 'react';
import { cn } from '@/lib/utils';
import { formatRelativeTime } from '@/lib/utils';
import {
  IconBell,
  IconShipments,
  IconManifests,
  IconUser,
} from '@/components/icons/Icons';
import type { Notification, NotificationType } from '@/types/models';
import styles from './NotificationItem.module.css';

interface NotificationItemProps {
  /** The notification data to display. */
  notification: Notification;
  /** Called when the item is clicked. */
  onClick?: (notification: Notification) => void;
}

/** Maps notification types to appropriate icons. */
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'invitation':
    case 'member_joined':
      return <IconUser width={20} height={20} />;
    case 'shipment_status':
      return <IconShipments width={20} height={20} />;
    case 'manifest_status':
      return <IconManifests width={20} height={20} />;
    case 'discrepancy':
      return <IconBell width={20} height={20} />;
    default:
      return <IconBell width={20} height={20} />;
  }
}

export function NotificationItem({ notification, onClick }: NotificationItemProps) {
  const handleClick = useCallback(() => {
    onClick?.(notification);
  }, [notification, onClick]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick?.(notification);
      }
    },
    [notification, onClick],
  );

  return (
    <button
      className={cn(styles.item, !notification.read && styles.unread)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      type="button"
      aria-label={`${notification.title}${!notification.read ? ' (unread)' : ''}`}
    >
      <span className={styles.iconWrapper} aria-hidden="true">
        {getNotificationIcon(notification.type)}
      </span>

      <div className={styles.content}>
        <div className={styles.titleRow}>
          <p className={styles.title}>{notification.title}</p>
          {!notification.read && (
            <span className={styles.unreadDot} aria-hidden="true" />
          )}
        </div>

        <p className={styles.body}>{notification.body}</p>
        <p className={styles.time}>
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>
    </button>
  );
}
